import { useState, type ReactNode } from 'react';
import { flexRender, getCoreRowModel, getSortedRowModel, useReactTable, type ColumnDef, type SortingState, type Updater } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import { ArrowDown, ArrowUp, Columns3 } from 'lucide-react';
import { cn } from './ui';

export interface DataTableProps<T> {
  columns: ColumnDef<T, unknown>[];
  data: T[];
  loading?: boolean;
  /** Server-side pagination meta; when omitted the table shows all rows. */
  page?: { page: number; page_size: number; total: number; onChange: (page: number, pageSize: number) => void };
  /** Server-side sort (generic list contract): `field` / `-field` / '' for the server default. Column ids are the sort fields. */
  sort?: { value: string; onChange: (sort: string) => void };
  toolbar?: ReactNode;
  emptyText?: string;
  onRowClick?: (row: T) => void;
}

function toSortingState(sort: string): SortingState {
  if (!sort) return [];
  const desc = sort.startsWith('-');
  return [{ id: desc ? sort.slice(1) : sort, desc }];
}

/** DataTable (MRLCO standard): sorting (client or server), column toggle, server pagination, RTL-safe. */
export function DataTable<T>({ columns, data, loading, page, sort, toolbar, emptyText, onRowClick }: DataTableProps<T>) {
  const { t } = useTranslation();
  const [clientSorting, setClientSorting] = useState<SortingState>([]);
  const [showColumns, setShowColumns] = useState(false);
  const sorting = sort ? toSortingState(sort.value) : clientSorting;
  const onSortingChange = (updater: Updater<SortingState>) => {
    const next = typeof updater === 'function' ? updater(sorting) : updater;
    if (sort) {
      const first = next[0];
      sort.onChange(first ? `${first.desc ? '-' : ''}${first.id}` : '');
    } else {
      setClientSorting(next);
    }
  };
  const table = useReactTable({
    data,
    columns,
    state: { sorting },
    onSortingChange,
    manualSorting: Boolean(sort),
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });
  const from = page ? (page.page - 1) * page.page_size + 1 : 1;
  const to = page ? Math.min(page.page * page.page_size, page.total) : data.length;
  const pages = page ? Math.max(1, Math.ceil(page.total / page.page_size)) : 1;

  return (
    <div className="card">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-neutral-200 px-3 py-2 dark:border-neutral-800">
        <div className="flex flex-wrap items-center gap-2">{toolbar}</div>
        <div className="relative">
          <button type="button" className="btn-secondary px-2 py-1" onClick={() => setShowColumns((s) => !s)} aria-label="columns">
            <Columns3 size={16} />
          </button>
          {showColumns && (
            <div className="card absolute end-0 z-20 mt-1 w-56 p-2 text-sm">
              {table.getAllLeafColumns().map((c) => (
                <label key={c.id} className="flex items-center gap-2 py-1">
                  <input type="checkbox" checked={c.getIsVisible()} onChange={c.getToggleVisibilityHandler()} />
                  <span>{typeof c.columnDef.header === 'string' ? c.columnDef.header : c.id}</span>
                </label>
              ))}
            </div>
          )}
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-neutral-50 text-xs uppercase text-neutral-500 dark:bg-neutral-900/60 dark:text-neutral-400">
            {table.getHeaderGroups().map((hg) => (
              <tr key={hg.id}>
                {hg.headers.map((h) => (
                  <th key={h.id} className="px-3 py-2 text-start font-medium">
                    {h.isPlaceholder ? null : (
                      <button type="button" className={cn('inline-flex items-center gap-1', h.column.getCanSort() && 'cursor-pointer select-none')} onClick={h.column.getToggleSortingHandler()}>
                        {flexRender(h.column.columnDef.header, h.getContext())}
                        {h.column.getIsSorted() === 'asc' && <ArrowUp size={12} />}
                        {h.column.getIsSorted() === 'desc' && <ArrowDown size={12} />}
                      </button>
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={columns.length} className="px-3 py-8 text-center text-neutral-500">
                  {t('app.loading')}
                </td>
              </tr>
            )}
            {!loading && data.length === 0 && (
              <tr>
                <td colSpan={columns.length} className="px-3 py-8 text-center text-neutral-500">
                  {emptyText ?? t('app.noResults')}
                </td>
              </tr>
            )}
            {!loading &&
              table.getRowModel().rows.map((row) => (
                <tr key={row.id} onClick={() => onRowClick?.(row.original)} className={cn('border-t border-neutral-100 dark:border-neutral-800', onRowClick && 'cursor-pointer hover:bg-neutral-50 dark:hover:bg-neutral-800/60')}>
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id} className="px-3 py-2 align-middle">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))}
          </tbody>
        </table>
      </div>
      {page && (
        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-neutral-200 px-3 py-2 text-xs text-neutral-600 dark:border-neutral-800 dark:text-neutral-400">
          <span>{page.total ? t('app.showing', { from, to, total: page.total }) : ''}</span>
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-1">
              {t('app.rowsPerPage')}
              <select className="input w-auto py-1" value={page.page_size} onChange={(e) => page.onChange(1, Number(e.target.value))}>
                {[10, 25, 50, 100].map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </label>
            <button type="button" className="btn-secondary px-2 py-1" disabled={page.page <= 1} onClick={() => page.onChange(page.page - 1, page.page_size)}>
              {t('app.previous')}
            </button>
            <span>
              {page.page} / {pages}
            </span>
            <button type="button" className="btn-secondary px-2 py-1" disabled={page.page >= pages} onClick={() => page.onChange(page.page + 1, page.page_size)}>
              {t('app.next')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
